import { createFileRoute } from "@tanstack/react-router";
import { useMemo } from "react";

import { SalesShell } from "@/components/crm/SalesShell";
import { HubspotImportWizard } from "@/components/crm/import/hubspot/HubspotImportWizard";
import { ImportHistoryPanel } from "@/components/crm/import/ImportHistoryPanel";
import { EmptyState } from "@/components/crm/parts";
import { useAccounts, useContracts, useStages } from "@/lib/crm/useCrm";
import { useImportedDeals } from "@/lib/crm/useHubspotImport";
import { usePermissions } from "@/lib/crm/usePermissions";
import type { AccountRef } from "@/lib/crm/import/hubspot/classify";
import type { ContractRef } from "@/lib/crm/import/hubspot/match";
import type { StageRef } from "@/lib/crm/import/hubspot/plan";

export const Route = createFileRoute("/_authenticated/sales/admin/import-hubspot")({
  head: () => ({
    meta: [
      { title: "Import HubSpot deals — MyPriorities" },
      {
        name: "description",
        content:
          "Admin-only HubSpot import: apply exclusion rules, normalise stages, match existing contracts and review every deal before anything is created.",
      },
      { property: "og:title", content: "Import HubSpot deals — MyPriorities" },
      {
        property: "og:description",
        content:
          "Upload an All Deals export, see exactly what will be created, then confirm. Existing contracts are never overwritten.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: HubspotImportPage,
});

function HubspotImportPage() {
  return (
    <SalesShell
      title="Import HubSpot deals"
      subtitle="Admin only. Sales history and current pipeline from your HubSpot All Deals export."
    >
      {({ orgId, role }) => <Body orgId={orgId} isAdmin={role === "admin"} />}
    </SalesShell>
  );
}

function Body({ orgId, isAdmin }: { orgId: string; isAdmin: boolean }) {
  const { can, ready } = usePermissions();
  const accounts = useAccounts(orgId);
  const contracts = useContracts(orgId);
  const stages = useStages(orgId);
  const existingDeals = useImportedDeals(orgId);

  const accountRefs = useMemo<AccountRef[]>(
    () => (accounts.data ?? []).map((a) => ({ id: a.id, name: a.name })),
    [accounts.data],
  );

  const contractRefs = useMemo<ContractRef[]>(
    () =>
      (contracts.data ?? []).map((c) => ({
        id: c.id,
        accountId: c.account_id,
        name: c.name,
        startDate: c.start_date,
        endDate: c.end_date,
        annualValue: Number(c.annual_value ?? 0),
        totalValue: Number(c.total_value ?? 0),
      })),
    [contracts.data],
  );

  const stageRefs = useMemo<StageRef[]>(
    () =>
      (stages.data ?? []).map((s) => ({
        id: s.id,
        name: s.name,
        probability: Number(s.probability ?? 0),
      })),
    [stages.data],
  );

  if (ready && (!isAdmin || !can("import_hubspot"))) {
    return (
      <EmptyState
        title="Admins only"
        body="The HubSpot import writes commercial history across the whole organisation, so it's limited to admins with “Import HubSpot deals” switched on in Sales settings."
      />
    );
  }

  return (
    <div className="grid gap-5">
      <HubspotImportWizard
        orgId={orgId}
        accounts={accountRefs}
        contracts={contractRefs}
        stages={stageRefs}
        existing={existingDeals.data ?? []}
      />
      <ImportHistoryPanel orgId={orgId} />
    </div>
  );
}
